import api from "../../../Services/api";
import React, { useEffect, useState, useCallback } from "react";
import MenuLateral from "../../../Componentes/MenuLateral";
import MenuSuperior from "../../../Componentes/MenuSuperior";
import { useAuthContext } from "../../../context/authContext";
import { useOperador } from "../../../Hooks/useOperador";
import { useNavigate, useParams } from "react-router-dom";
import CardQuestao from "../../../Componentes/chkQuestao/CardQuestao";
import debounce from "lodash.debounce";
import {
  FiSearch,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
  FiXCircle,
  FiCheckCircle,
} from "react-icons/fi";
import { IoReturnDownBackOutline } from "react-icons/io5";
import "./acompanhamentoQuestao.css";

const AcompanhamentoQuestao = () => {

  const { IDSecao } = useParams();

  const [questoes, setQuestoes] = useState([]);
  const [secao, setSecao] = useState(null);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const limit = 20;

  // ❗filtro digitado e filtro aplicável são separados
  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");

  const [loading, setLoading] = useState(false);

  const { authReady } = useAuthContext();
  const { IDOperador, nome, operador } = useOperador();

  const navigate = useNavigate();
  let idFormatado = String(IDOperador).padStart(6, '0');
  const secaoId = Number.isNaN(Number(IDSecao)) ? null : Number(IDSecao);

  const handleDebounce = useCallback(
    debounce((value) => {
      setDebouncedFilter(value);
      setPage(1); // Sempre volta pra primeira página quando filtra
    }, 500),
    []
  );

  // Quando o usuário digita → dispara debounce
  const onChangeFiltro = (e) => {
    const value = e.target.value;
    setFilter(value);
    handleDebounce(value);
  };

  useEffect(() => {
    if (authReady) {
      if (!operador) {
        navigate("/");
      }
    }
  }, [authReady, operador, secaoId]);

  useEffect(() => {
    if (secaoId) fetchSecao();
  }, [secaoId]);

  useEffect(() => {
    fetchQuestoes();
  }, [page, debouncedFilter, secaoId]);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilter]);


  useEffect(() => {
    return () => {
      handleDebounce.cancel();
    };
  }, [handleDebounce]);

  const clearFilter = () => {
    setFilter("");
    setDebouncedFilter("");
    setPage(1);
  };

  const handleVoltar = () => {
    if (secao?.IDCliente) {
      navigate(`/AuditoriaSecao/${secao.IDCliente}`);
    }
  };

  const buscaPorSecao = async ({ page = 1, limit = 20, IDSecao: IDSecaoParam, filter = "" } = {}) => {
    const id = IDSecaoParam || secaoId;
    const response = await api.get(`/chkquestaosecao/secao`, {
      params: { page, limit, IDSecao: Number(id), filter },
    });
    setQuestoes(response.data.data);
    setTotal(response.data.total);

    return response.data.data;
  };

  const fetchSecao = async () => {
    try {
      setLoading(true);
      if (!secaoId) return;

      const response = await api.get(`/chksecao/${secaoId}`);
      const secaoData = response.data;
      setSecao(secaoData);
      fetchSecaoStats(secaoData);
    } finally {
      setLoading(false);
    }
  };

  const fetchSecaoStats = async (secaoData) => {
    if (!secaoData?.IDCliente || !secaoData?.descricao) return;

    try {
      const response = await api.get("/chksecoesstats", {
        params: {
          page: 1,
          limit: 50,
          IDCliente: secaoData.IDCliente,
          filter: secaoData.descricao,
        },
      });

      const match = response.data?.data?.find(
        (item) => Number(item.IDSecao) === Number(secaoId)
      );

      if (match) {
        setSecao((prev) => ({
          ...prev,
          qntQuestoes: Number(match.qntQuestoes) || 0,
          qntRespostas: Number(match.qntRespostas) || 0,
          qntQuestoesInativas: Number(match.qntQuestoesInativas) || 0,
          qntRespostasInativas: Number(match.qntRespostasInativas) || 0,
          qntPendentesInativas: Number(match.qntPendentesInativas) || 0,
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar estatisticas da secao", error);
    }
  };

  const fetchQuestoes = async () => {
    try {
      setLoading(true);

      // Buscar questões por seção
      // /AuditoriaQuestao/0/:IDSecao
      // -----------------------------------
      if (secaoId) {
        return buscaPorSecao({ page, limit, IDSecao: secaoId, filter: debouncedFilter, });
      }
    } catch (error) {
      console.error("Erro ao carregar questões", error);
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / limit);
  const totalSecao = secao?.qntQuestoes ?? total ?? 0;
  const respondidasSecao = secao?.qntRespostas ?? 0;
  const pendentesSecao = Math.max(totalSecao - respondidasSecao, 0);
  const inativasTotal = Number(secao?.qntQuestoesInativas || 0);
  const inativasRespondidas = Number(secao?.qntRespostasInativas || 0);
  const inativasPendentes = Number(secao?.qntPendentesInativas || 0);
  const hasInativas =
    inativasTotal > 0 || inativasRespondidas > 0 || inativasPendentes > 0;
  const totalAtivas = Math.max(totalSecao - inativasTotal, 0);
  const respondidasAtivas = Math.max(respondidasSecao - inativasRespondidas, 0);
  const percentualConcluido = totalAtivas > 0
    ? Math.round((respondidasAtivas / totalAtivas) * 100)
    : 0;
  const situacaoSecao = secao?.situacao ?? 1;
  const situacaoTexto = situacaoSecao === 2
    ? "Finalizado"
    : situacaoSecao === 0
      ? "Desativado"
      : "Em andamento";
  const situacaoClasse = situacaoSecao === 2
    ? "ac-stat-finalizado"
    : situacaoSecao === 0
      ? "ac-stat-desativado"
      : "ac-stat-andamento";
  const situacaoIcon = situacaoSecao === 2 ? (
    <FiCheckCircle />
  ) : situacaoSecao === 0 ? (
    <FiXCircle />
  ) : (
    <FiLoader />
  );

  return (
    <div className="menu-container-menu">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <MenuSuperior userId={idFormatado} userName={nome} />

        <div className="mb-4">
          <h4 style={{ width: '100%', textAlign: 'center', paddingTop: '30px' }}>
            ACOMPANHAMENTO DE QUESTÕES - AUDITORIA
          </h4>

          {secao && !loading &&
            <div className="ac-subtitle">
              <h5>{secao.descricao}</h5>
            </div>
          }
          {secao && !loading && (
            <div className="ac-summary">
              <div className="ac-summary-progress">
                <div className="ac-progress-track">
                  <div
                    className="ac-progress-fill"
                    style={{ width: `${percentualConcluido}%` }}
                  />
                </div>
                <span className="ac-progress-label">{percentualConcluido}% concluído</span>
              </div>
              <div className="ac-summary-stats">
                <div className="ac-stat">
                  <span className="ac-stat-label">Total de questões</span>
                  <span className="ac-stat-value">{totalSecao}</span>
                  {hasInativas && (
                    <span className="ac-stat-label-sub">
                      {inativasTotal > 0 ? `(${inativasTotal} inativas)` : ""}
                    </span>
                  )}
                </div>
                <div className="ac-stat">
                  <span className="ac-stat-label">Respondidas</span>
                  <span className="ac-stat-value">{respondidasSecao}</span>
                  {hasInativas && (
                    <span className="ac-stat-label-sub">
                      {inativasRespondidas > 0
                        ? `(${inativasRespondidas} inativas)`
                        : ""}
                    </span>
                  )}
                </div>
                <div className="ac-stat">
                  <span className="ac-stat-label">Pendentes</span>
                  <span className="ac-stat-value">{pendentesSecao}</span>
                  {hasInativas && (
                    <span className="ac-stat-label-sub">
                      {inativasPendentes > 0
                        ? `(${inativasPendentes} inativas)`
                        : ""}
                    </span>
                  )}
                </div>
                <div className={`ac-stat ${situacaoClasse}`}>
                  <span className="ac-stat-label">Situacao</span>
                  <span className="ac-stat-value ac-stat-value-with-icon">
                    {situacaoIcon}
                    {situacaoTexto}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="container mt-1 px-5 py-2 acompanhamento-questao">

          {/* PARTE SUPERIOR */}
          <div className="menuListArea">
            <div className="btn-voltarArea">
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm btn-voltar"
                  onClick={handleVoltar}
                  disabled={!secao?.IDCliente}
                >
                  <IoReturnDownBackOutline size={18} style={{ marginRight: 6 }} />
                  Voltar
                </button>
              </div>
            </div>
            <div className="search-container">
              <span className="search-icon">
                <FiSearch />
              </span>
              <input
                type="text"
                className="search-input"
                placeholder="Buscar questão..."
                value={filter}
                onChange={onChangeFiltro}
              />
              {filter.length > 0 && (
                <button className="clear-btn" onClick={clearFilter}>
                  <FiX />
                </button>
              )}
            </div>

          </div>

          {loading && <p className="loading-text">Carregando...</p>}

          {!loading && !questoes.length && (
            <p className="empty-text">Nenhuma questão encontrada.</p>
          )}

          {!loading && questoes.length && (
            <>
              <ul className="questao-list">
                {questoes.map((questao) => (
                  <CardQuestao
                    key={questao.IDQuestao_Secao}
                    questao={questao}
                  />
                ))}
              </ul>


              <div className="pagination">
                <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1}>
                  <FiChevronLeft size={18} style={{ marginRight: 4 }} />
                </button>

                <span>Página {page} de {totalPages}</span>

                <button onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>
                  <FiChevronRight size={18} style={{ marginLeft: 4 }} />
                </button>
              </div>
            </>
          )}
        </div>

      </div >
    </div >
  );
};

export default AcompanhamentoQuestao;
